import { HEADWEAR, NECKWEAR, type MascotProfile } from './mascotProfile';
import type { CareReward } from './petCare';

export type PetItemId = `neckwear:${Exclude<MascotProfile['neckwear'], 'none'>}` | `headwear:${Exclude<MascotProfile['headwear'], 'none'>}`;
export type PetMilestone = 'workDays' | 'records' | 'focusMinutes' | 'bond';
export interface PetItem {
  id: PetItemId; slot: 'neckwear' | 'headwear'; value: string; label: string;
  collection: 'На каждый день' | 'Уют' | 'Рабочий день' | 'Приключения' | 'Сказки';
  price: number; requirement?: { kind: PetMilestone; value: number; label: string };
}
const details: Record<PetItemId, Pick<PetItem, 'collection' | 'price' | 'requirement'>> = {
  'neckwear:collar': { collection: 'На каждый день', price: 8 },
  'neckwear:bow': { collection: 'На каждый день', price: 12 },
  'neckwear:bandana': { collection: 'На каждый день', price: 12 },
  'neckwear:vampire': { collection: 'Сказки', price: 64, requirement: { kind: 'workDays', value: 3, label: '3 рабочих дня вместе' } },
  'neckwear:scarf': { collection: 'Уют', price: 18 },
  'neckwear:tie': { collection: 'Рабочий день', price: 24 },
  'neckwear:sailor': { collection: 'Приключения', price: 28 },
  'neckwear:sweater': { collection: 'Уют', price: 32, requirement: { kind: 'bond', value: 5, label: '5 сердечек дружбы' } },
  'neckwear:medal': { collection: 'Рабочий день', price: 48, requirement: { kind: 'records', value: 20, label: '20 записей сохранено' } },
  'neckwear:hero': { collection: 'Сказки', price: 72, requirement: { kind: 'focusMinutes', value: 120, label: '120 минут работы вместе' } },
  'headwear:flower': { collection: 'На каждый день', price: 8 },
  'headwear:beanie': { collection: 'Уют', price: 18 },
  'headwear:crown': { collection: 'Сказки', price: 96, requirement: { kind: 'workDays', value: 5, label: '5 рабочих дней вместе' } },
  'headwear:beret': { collection: 'Рабочий день', price: 20 },
  'headwear:wizard': { collection: 'Сказки', price: 60, requirement: { kind: 'bond', value: 10, label: '10 сердечек дружбы' } },
  'headwear:party': { collection: 'На каждый день', price: 16 },
  'headwear:cap': { collection: 'Приключения', price: 20 },
  'headwear:headphones': { collection: 'Рабочий день', price: 36, requirement: { kind: 'focusMinutes', value: 30, label: '30 минут работы вместе' } },
  'headwear:sleepcap': { collection: 'Уют', price: 24 },
  'headwear:pirate': { collection: 'Приключения', price: 44, requirement: { kind: 'workDays', value: 2, label: '2 рабочих дня вместе' } },
  'neckwear:raincoat': { collection: 'На каждый день', price: 28 },
  'neckwear:kimono': { collection: 'Сказки', price: 70, requirement: { kind: 'bond', value: 15, label: '15 сердечек дружбы' } },
  'neckwear:explorer': { collection: 'Приключения', price: 38 },
  'neckwear:stargazer': { collection: 'Сказки', price: 100, requirement: { kind: 'focusMinutes', value: 180, label: '180 минут работы вместе' } },
  'neckwear:royal': { collection: 'Сказки', price: 140, requirement: { kind: 'workDays', value: 7, label: '7 рабочих дней вместе' } },
  'neckwear:overalls': { collection: 'Рабочий день', price: 34, requirement: { kind: 'records', value: 8, label: '8 записей сохранено' } },
  'headwear:chef': { collection: 'Рабочий день', price: 26 },
  'headwear:cowboy': { collection: 'Приключения', price: 38 },
  'headwear:panama': { collection: 'На каждый день', price: 22 },
  'headwear:aviator': { collection: 'Приключения', price: 68, requirement: { kind: 'focusMinutes', value: 90, label: '90 минут работы вместе' } },
  'headwear:unicorn': { collection: 'Сказки', price: 90, requirement: { kind: 'bond', value: 20, label: '20 сердечек дружбы' } },
  'headwear:leafcrown': { collection: 'Уют', price: 30, requirement: { kind: 'bond', value: 3, label: '3 сердечка дружбы' } },
};
export const PET_ITEMS: PetItem[] = (['neckwear', 'headwear'] as const).flatMap(slot =>
  (slot === 'neckwear' ? NECKWEAR : HEADWEAR).filter(item => item.id !== 'none').map(item => {
    const id = `${slot}:${item.id}` as PetItemId;
    return { id, slot, value: item.id, label: item.label, ...details[id] };
  }));
export const FOCUS_BLOCK_MS = 5 * 60_000;
export const DAILY_FOCUS_BLOCKS = 6;
export const DAILY_RECORDS = 8;
export const FOCUS_REWARD = 4;
export const RECORD_REWARD = 2;
const CARE_ACTIONS: CareReward[] = ['feed', 'play', 'pet', 'groom', 'comfort'];
export interface PetProgress {
  version: 1; balance: number; earned: number; owned: PetItemId[];
  totals: Record<PetMilestone, number>;
  day: { date: string; focusMs: number; records: string[]; care: CareReward[]; counted: boolean };
}
export function petDay(now = Date.now()): string {
  const date = new Date(now);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
export function defaultPetProgress(now = Date.now()): PetProgress {
  return { version: 1, balance: 0, earned: 0, owned: [], totals: { workDays: 0, records: 0, focusMinutes: 0, bond: 0 },
    day: { date: petDay(now), focusMs: 0, records: [], care: [], counted: false } };
}
const number = (value: unknown, max = 1_000_000) => typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.min(max, value)) : 0;
const object = (value: unknown): Record<string, unknown> => value && typeof value === 'object' ? value as Record<string, unknown> : {};
export function normalizePetProgress(value: unknown, now = Date.now()): PetProgress {
  const raw = object(value), totals = object(raw.totals), day = object(raw.day), result = defaultPetProgress(now);
  result.earned = Math.floor(number(raw.earned));
  result.balance = Math.min(result.earned, Math.floor(number(raw.balance)));
  result.owned = [...new Set(Array.isArray(raw.owned) ? raw.owned.filter((id): id is PetItemId => PET_ITEMS.some(item => item.id === id)) : [])];
  for (const key of Object.keys(result.totals) as PetMilestone[]) result.totals[key] = number(totals[key]);
  if (typeof day.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(day.date) && day.date >= petDay(now)) {
    // Откат часов не открывает ещё одну дневную награду.
    result.day = { date: day.date, focusMs: number(day.focusMs, FOCUS_BLOCK_MS * DAILY_FOCUS_BLOCKS),
      records: [...new Set(Array.isArray(day.records) ? day.records.filter((id): id is string => typeof id === 'string' && /^[a-f0-9]{8}$/.test(id)) : [])].slice(0, DAILY_RECORDS),
      care: [...new Set(Array.isArray(day.care) ? day.care.filter((action): action is CareReward => CARE_ACTIONS.includes(action as CareReward)) : [])], counted: day.counted === true };
  }
  return result;
}
export function itemOwned(progress: PetProgress, slot: PetItem['slot'], value: string): boolean {
  return value === 'none' || progress.owned.includes(`${slot}:${value}` as PetItemId);
}
export function itemRequirementMet(progress: PetProgress, item: PetItem): boolean {
  return !item.requirement || progress.totals[item.requirement.kind] >= item.requirement.value;
}
/** Сохраняем лишь уже надетые старые вещи при первом запуске коллекции. */
export function adoptExistingOutfit(progress: PetProgress, profile: MascotProfile): PetProgress {
  return { ...progress, owned: PET_ITEMS.filter(item => progress.owned.includes(item.id) || profile[item.slot] === item.value).map(item => item.id) };
}
function markDay(progress: PetProgress): void {
  if (!progress.day.counted) { progress.day.counted = true; progress.totals.workDays++; }
}
function credit(progress: PetProgress, amount: number): void { progress.balance += amount; progress.earned += amount; }
export function addWorkTime(value: PetProgress, elapsed: number, now = Date.now()): PetProgress {
  const next = normalizePetProgress(value, now);
  const before = next.day.focusMs;
  next.day.focusMs = Math.min(FOCUS_BLOCK_MS * DAILY_FOCUS_BLOCKS, before + number(elapsed, 15_000));
  next.totals.focusMinutes += (next.day.focusMs - before) / 60_000;
  credit(next, (Math.floor(next.day.focusMs / FOCUS_BLOCK_MS) - Math.floor(before / FOCUS_BLOCK_MS)) * FOCUS_REWARD);
  if (next.day.focusMs >= FOCUS_BLOCK_MS) markDay(next);
  return next;
}
/** Односторонний отпечаток для дедупликации. Имена, поля и значения CRM не сохраняются. */
export function workFingerprint(key: string): string {
  let hash = 2166136261;
  for (const char of key) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
  return (hash >>> 0).toString(16).padStart(8, '0');
}
export function addWorkRecord(value: PetProgress, key: string, now = Date.now()): PetProgress {
  const next = normalizePetProgress(value, now), id = workFingerprint(key);
  if (!key || next.day.records.includes(id) || next.day.records.length >= DAILY_RECORDS) return next;
  next.day.records.push(id); next.totals.records++; credit(next, RECORD_REWARD); markDay(next);
  return next;
}
export function addCareBond(value: PetProgress, action: CareReward, now = Date.now()): PetProgress {
  const next = normalizePetProgress(value, now);
  if (!next.day.care.includes(action)) { next.day.care.push(action); next.totals.bond++; }
  return next;
}
/** Проверка комплекта атомарна: повторное открытие и недостаток средств ничего не списывают. */
export function purchasePetItems(value: PetProgress, ids: PetItemId[], now = Date.now()): PetProgress | null {
  const next = normalizePetProgress(value, now);
  const items = [...new Set(ids)].filter(id => !next.owned.includes(id)).map(id => PET_ITEMS.find(item => item.id === id));
  if (items.some(item => !item || !itemRequirementMet(next, item))) return null;
  const price = items.reduce((sum, item) => sum + item!.price, 0);
  if (next.balance < price) return null;
  next.balance -= price; next.owned.push(...items.map(item => item!.id));
  return next;
}
